/**
 * LE DÉCOUPAGE D'UN NŒUD EN FEUILLETS (design §3).
 *
 * Un nœud fait 250 à 350 mots. Sur un téléphone, cela fait quatre écrans, et un joueur qui doit
 * faire défiler quatre écrans pour retrouver ses choix lit le texte en diagonale. On le coupe donc
 * en feuillets d'une hauteur d'écran environ — et l'on tient trois règles :
 *
 * 1. **On coupe ENTRE les paragraphes** — et, quand il n'y en a qu'un, ENTRE DEUX PHRASES. Ce qu'on
 *    ne fait jamais, c'est couper DANS une phrase : une phrase coupée en deux est une faute
 *    d'imprimeur, et c'est plus grave qu'un feuillet un peu long. Le repli par phrases n'est pas
 *    théorique : le brief demande trois à cinq paragraphes par nœud, mais une aventure importée
 *    avant cette règle — ou un modèle qui l'oublie — arrive d'un seul tenant, et l'écran de lecture
 *    doit rester tenable.
 * 2. **Un nœud court n'a qu'un feuillet**, et alors rien ne s'affiche : une pagination « 1 / 1 »
 *    est une pagination qui ment sur le travail qu'elle fait.
 * 3. **L'AMORCE SORT DU TEXTE.** Le dernier paragraphe — celui qui pose la situation à trancher —
 *    n'est pas un feuillet : il vit dans la zone de décision, au-dessus des choix, en permanence.
 *    C'est ce que demande la séparation contexte / décision : on lit le contexte, on décide devant
 *    la question. La répéter aux deux endroits l'aurait affichée deux fois ; la laisser au fond du
 *    texte aurait obligé à remonter pour se rappeler ce qu'on tranche.
 *
 * TOUT EST PUR ICI, et c'est ce qui rend la règle vérifiable : le découpage se teste sur des
 * chaînes, sans navigateur, sans React, sans hauteur mesurée. Une mise en page qui dépend d'une
 * mesure réelle est une mise en page qu'on ne peut plus prouver.
 */

/** La longueur visée d'un feuillet, en caractères, quand on ne sait rien de la fenêtre. */
export const MESURE_DUN_FEUILLET = 520;

/**
 * LA LARGEUR À PARTIR DE LAQUELLE LA DÉCISION PASSE À DROITE DU TEXTE (design §3). C'est le `76rem`
 * de la feuille de style, en pixels — le seul endroit du front où une valeur de mise en page se
 * répète, parce que le découpage doit savoir dans quelle partition il coupe et que le CSS ne sait
 * pas le lui dire sans qu'on mesure quelque chose.
 */
export const LARGEUR_DEUX_COLONNES = 1216;

/**
 * LA LONGUEUR D'UN FEUILLET POUR CETTE FENÊTRE-CI.
 *
 * Un feuillet de six cents caractères tient dans une colonne de bureau et déborde de moitié sur un
 * téléphone : la même mesure partout donne, d'un côté un écran à moitié vide, de l'autre un
 * feuillet qu'il faut faire défiler — c'est-à-dire les deux défauts qu'on répare.
 *
 * ELLE RESTE UNE FONCTION PURE DE DEUX NOMBRES, et c'est ce qui compte : on ne mesure aucun élément,
 * on ne lit aucune hauteur rendue, on n'attend aucun cycle de rendu. Deux entiers entrent, un entier
 * sort, et la règle se prouve sur des chaînes. Une mise en page qui dépendrait d'un `offsetHeight`
 * serait une mise en page qu'on ne pourrait plus prouver.
 *
 * Les deux constantes viennent du §2 : la colonne fait au plus 64 caractères, et une ligne à 19 px
 * sur un interligne de 1,7 fait une trentaine de pixels. Le tiers de la fenêtre est ce qui reste à
 * la lecture une fois la tranche, le bandeau, le titre, le pied et la décision servis.
 */
export function mesureDeFeuillet(largeur: number, hauteur: number, echelle = 1): number {
  // L'ÉCHELLE DU TEXTE ENTRE ICI (design §5). Des lettres plus grosses, ce sont moins de caractères
  // par ligne ET moins de lignes par écran : le feuillet rétrécit donc au carré de l'échelle. Sans
  // ce facteur, le cran « immense » redonnerait le mur de texte que les feuillets ont défait.
  const e = Math.max(0.5, echelle);
  const parLigne = Math.min(64, Math.max(24, Math.round(largeur / (10 * e))));
  // CINQ LIGNES AU MOINS, quelle que soit la fenêtre : sous ce plancher, on tourne la page plus
  // souvent qu'on ne lit, et la pagination devient le sujet.
  const lignes = Math.max(5, Math.round((hauteur * partDeLaPage(largeur)) / (30 * e)));
  return parLigne * lignes;
}

/**
 * LA PART DE LA FENÊTRE QUI REVIENT AU TEXTE, et pourquoi elle dépend de la largeur.
 *
 * En colonne, la page partage la hauteur avec la tranche et la zone de décision : il lui en reste
 * le tiers. Au-delà de `LARGEUR_DEUX_COLONNES`, la décision passe À CÔTÉ du texte (design §3) et
 * la page garde toute la hauteur moins la tranche — c'est là qu'un tiers devenait un demi-écran
 * vide sous les dernières lignes.
 *
 * LE CHIFFRE LARGE EST UN COMPROMIS ASSUMÉ, et il ne peut pas ne pas l'être : le premier feuillet
 * paie le bandeau et le titre, les suivants non. Réglé sur le premier, tous les autres seraient à
 * moitié vides ; réglé sur les suivants, le premier déborderait d'un tiers d'écran. On se place
 * entre les deux, et l'estompe du bas de page dit ce qui dépasse.
 */
function partDeLaPage(largeur: number): number {
  return largeur >= LARGEUR_DEUX_COLONNES ? 0.56 : 0.34;
}

/** Les paragraphes d'un texte de nœud : ils s'y séparent par une ligne vide. */
export function paragraphesDe(texte: string): string[] {
  return texte
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter((p) => p.length > 0);
}

/**
 * LES PHRASES D'UN PARAGRAPHE. On coupe après un point, un point d'exclamation, un point
 * d'interrogation ou des points de suspension SUIVIS D'UNE ESPACE — sans quoi « M. Corbeau » ferait
 * deux phrases, et « 3.5 » aussi.
 */
function phrasesDe(paragraphe: string): string[] {
  const morceaux = paragraphe.match(/[^.!?…]+[.!?…]+(?=\s|$)|[^.!?…]+$/g);
  return (morceaux ?? [paragraphe]).map((p) => p.trim()).filter(Boolean);
}

/**
 * UN PARAGRAPHE TROP LONG, DÉBITÉ EN MORCEAUX QUI TIENNENT. Chaque morceau reste une suite de
 * phrases entières ; on ne coupe jamais dans l'une d'elles, même si elle dépasse à elle seule.
 */
function debite(paragraphe: string, mesure: number): string[] {
  if (paragraphe.length <= mesure) return [paragraphe];
  const morceaux: string[] = [];
  let courant = "";
  for (const phrase of phrasesDe(paragraphe)) {
    if (courant && courant.length + phrase.length > mesure) {
      morceaux.push(courant);
      courant = "";
    }
    courant = courant ? `${courant} ${phrase}` : phrase;
  }
  if (courant) morceaux.push(courant);
  return morceaux;
}

/**
 * LES FEUILLETS. Chacun est une liste de paragraphes ; on empile tant qu'on tient dans la mesure,
 * et l'on tourne la page quand le paragraphe suivant ferait déborder.
 */
export function feuilletsDe(texte: string, mesure = MESURE_DUN_FEUILLET): string[][] {
  const paragraphes = contexteDe(texte).flatMap((p) => debite(p, mesure));
  if (paragraphes.length === 0) return [];

  const feuillets: string[][] = [];
  let courant: string[] = [];
  let longueur = 0;

  for (const paragraphe of paragraphes) {
    const deborde = longueur > 0 && longueur + paragraphe.length > mesure;
    if (deborde) {
      feuillets.push(courant);
      courant = [];
      longueur = 0;
    }
    courant.push(paragraphe);
    longueur += paragraphe.length;
  }
  if (courant.length > 0) feuillets.push(courant);

  return feuillets;
}

/**
 * L'AMORCE : le dernier paragraphe du texte, celui qui pose la question.
 *
 * Ce n'est pas une donnée du §5.1 mais une lecture de la prose, et c'est assumé : la passe 7 écrit
 * un corps qui se termine sur la situation à trancher — c'est la forme même du livre-jeu. Le jour
 * où un nœud portera un champ « décision » explicite, cette fonction cédera la place sans que
 * l'interface bouge.
 *
 * Un texte d'un seul paragraphe n'a PAS d'amorce séparée : la répéter en tête des choix reviendrait
 * à afficher deux fois le nœud entier.
 */
export function amorceDe(texte: string): string | null {
  const paragraphes = paragraphesDe(texte);
  if (paragraphes.length === 0) return null;
  if (paragraphes.length > 1) return paragraphes[paragraphes.length - 1]!;

  // Un nœud d'un seul tenant : l'amorce est sa dernière phrase. Répéter le paragraphe entier
  // au-dessus des choix afficherait deux fois le nœud, ce qui est pire que de ne rien répéter.
  const phrases = phrasesDe(paragraphes[0]!);
  return phrases.length > 1 ? phrases[phrases.length - 1]! : null;
}

/**
 * LE CONTEXTE : le texte SANS son amorce. C'est ce qui se lit en feuillets ; l'amorce, elle, part
 * dans la zone de décision. Un nœud qui n'a pas d'amorce séparable garde tout son texte.
 */
export function contexteDe(texte: string): string[] {
  const paragraphes = paragraphesDe(texte);
  if (amorceDe(texte) === null) return paragraphes;
  if (paragraphes.length > 1) return paragraphes.slice(0, -1);

  // Un seul paragraphe : on lui retire sa dernière phrase, qui devient l'amorce.
  const phrases = phrasesDe(paragraphes[0]!);
  return [phrases.slice(0, -1).join(" ")];
}

const CHIFFRES_ROMAINS = ["", "i", "ii", "iii", "iv", "v", "vi", "vii", "viii", "ix", "x", "xi", "xii"];

/** Le numéro d'un feuillet, en petits chiffres romains comme dans un livre. Au-delà de douze, le
 * chiffre arabe : un nœud de treize feuillets serait de toute façon une anomalie de rédaction. */
export function numeroDeFeuillet(index: number): string {
  return CHIFFRES_ROMAINS[index + 1] ?? String(index + 1);
}

/**
 * CE QUE LA VOIX LIT, et rien d'autre (design §3). Ni « Acte 2 », ni les coûts des choix, ni les
 * mots de l'interface : le titre, puis le texte du feuillet affiché. Ce qui se lit à l'œil et ce
 * qui s'écoute ne sont pas le même découpage.
 */
export function aLireAHauteVoix(titre: string, feuillet: string[]): string {
  return [titre, ...feuillet].join(".\n\n");
}
